import { Fragment, useEffect, useState, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import Sidebar from "../Dashboard/Sidebar";
import Loader from "../layouts/Loader";
import MetaData from "../layouts/MetaData";
import { getAttendanceByDate, saveAttendanceBulk, getStaffMonthlyAttendance } from "../../actions/attendanceActions";
import { getAllStaff } from "../../actions/staffActions";
import { clearAttendanceSaved, clearAttendanceError } from "../../slices/attendanceSlice";
import formatDate, { toInputDate } from "../../utils/formatDate";
import { downloadAttendancePDF } from "../../utils/attendancePdf";
import { downloadPaySlipPDF } from "../../utils/paySlipPdf";
import AppDatePicker from "../common/AppDatePicker";

// Helper to reliably get today's local date (YYYY-MM-DD)
const getTodayDate = () => {
    const d = new Date();
    const day = String(d.getDate()).padStart(2, "0");
    const month = String(d.getMonth() + 1).padStart(2, "0");
    const year = d.getFullYear();
    return `${year}-${month}-${day}`;
};

// Robust phone sanitizer for WhatsApp (adds 91 for Indian numbers, strips non-digits & leading zeroes)
const sanitizeWhatsAppPhone = (phone) => {
    if (!phone) return "";
    let p = String(phone).replace(/\D/g, "");
    if (p.startsWith("0")) p = p.replace(/^0+/, "");
    if (p.length === 10) return `91${p}`;
    if (p.length === 12 && p.startsWith("91")) return p;
    return p;
};

// 4 Attendance Options as required:
// 1. Present: Full day worked (Paid)
// 2. Half Day: Present for half day only (0.5 day deduction)
// 3. Holiday: Festival holiday or Sunday off marked by admin (Paid)
// 4. Absent: Leave taken / absent (1.0 day deduction)
const STATUSES = ["Present", "Half Day", "Holiday", "Absent"];

const STATUS_CONFIG = {
    "Present": {
        label: "Present",
        short: "P",
        color: "#059669",
        bgLight: "#ecfdf5",
        border: "#a7f3d0",
        badgeClass: "badge-present",
        icon: "fa-check",
        desc: "Full Day Present"
    },
    "Half Day": {
        label: "Half Day",
        short: "HD",
        color: "#d97706",
        bgLight: "#fffbeb",
        border: "#fde68a",
        badgeClass: "badge-halfday",
        icon: "fa-adjust",
        desc: "Present Half Day (0.5 deduction)"
    },
    "Holiday": {
        label: "Holiday",
        short: "H",
        color: "#2563eb",
        bgLight: "#eff6ff",
        border: "#bfdbfe",
        badgeClass: "badge-holiday",
        icon: "fa-star",
        desc: "Holiday / Sunday Off (Paid)"
    },
    "Absent": {
        label: "Absent",
        short: "A",
        color: "#dc2626",
        bgLight: "#fef2f2",
        border: "#fecaca",
        badgeClass: "badge-absent",
        icon: "fa-times",
        desc: "Leave / Absent (1.0 deduction)"
    }
};

const WEEKDAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

export default function Attendance() {
    const dispatch = useDispatch();
    const { loading, records, monthly, isAttendanceSaved, error } = useSelector((state) => state.attendanceState);
    const { staffList } = useSelector((state) => state.staffState);
    const { user } = useSelector((state) => state.authState);
    const isAdmin = user?.role === "admin" || user?.role === "master";

    const [tab, setTab] = useState("mark"); // "mark" | "history"

    // ---- Mark Attendance State (Today fetched automatically) ----
    const [date, setDate] = useState(getTodayDate);
    const [marks, setMarks] = useState({});
    const [originalMarks, setOriginalMarks] = useState({});
    const [searchQuery, setSearchQuery] = useState("");
    const [statusFilter, setStatusFilter] = useState("all"); // "all" | "Present" | "Half Day" | "Holiday" | "Absent" | "unmarked"

    // ---- Monthly History State ----
    const now = new Date();
    const [historyStaffId, setHistoryStaffId] = useState("");
    const [year, setYear] = useState(now.getFullYear());
    const [month, setMonth] = useState(now.getMonth() + 1);

    // ---- Verification Modal State ----
    const [showVerifyModal, setShowVerifyModal] = useState(false);
    const [verifyPhone, setVerifyPhone] = useState("");
    const [verifyMessage, setVerifyMessage] = useState("");

    // Initial fetch of active staff
    useEffect(() => {
        dispatch(getAllStaff());
    }, [dispatch]);

    // Fetch attendance when date or tab changes
    useEffect(() => {
        if (tab === "mark" && date) {
            dispatch(getAttendanceByDate(date));
        }
    }, [dispatch, tab, date]);

    // Auto-select first staff member for history
    useEffect(() => {
        if (staffList?.length && !historyStaffId) {
            const firstStaff = staffList[0];
            setHistoryStaffId(firstStaff._id || firstStaff.id);
        }
    }, [staffList, historyStaffId]);

    // Fetch monthly records when staff, month, or year changes
    useEffect(() => {
        if (tab === "history" && historyStaffId) {
            dispatch(getStaffMonthlyAttendance(historyStaffId, year, month));
        }
    }, [dispatch, tab, historyStaffId, year, month]);

    // Sync marks state when records are received
    useEffect(() => {
        const initial = {};
        (records || []).forEach((r) => {
            const id = r?.staff?._id || r?.staff?.id;
            if (id) {
                initial[id] = r.status || null;
            }
        });
        setMarks(initial);
        setOriginalMarks(initial);
    }, [records]);

    // Handle toast alerts
    useEffect(() => {
        if (error) {
            toast(error, { type: "error" });
            dispatch(clearAttendanceError());
        }
        if (isAttendanceSaved) {
            toast("Attendance saved successfully!", { type: "success" });
            dispatch(clearAttendanceSaved());
            dispatch(getAttendanceByDate(date));
        }
    }, [error, isAttendanceSaved, dispatch, date]);

    // Date navigation helpers
    const changeDateByDays = (days) => {
        const [y, m, d] = date.split("-").map(Number);
        const current = new Date(y, m - 1, d);
        current.setDate(current.getDate() + days);
        setDate(toInputDate(current));
    };

    const isToday = useMemo(() => {
        return date === getTodayDate();
    }, [date]);

    // Single staff mark toggle
    const handleSetMark = (staffId, status) => {
        if (!isAdmin) return;
        setMarks((prev) => ({
            ...prev,
            [staffId]: prev[staffId] === status ? null : status
        }));
    };

    // Bulk actions
    const handleMarkAll = (status) => {
        if (!isAdmin) return;
        const updated = { ...marks };
        (records || []).forEach((r) => {
            const id = r?.staff?._id || r?.staff?.id;
            if (id) updated[id] = status;
        });
        setMarks(updated);
    };

    const handleClearAll = () => {
        if (!isAdmin) return;
        const updated = { ...marks };
        Object.keys(updated).forEach((id) => {
            updated[id] = null;
        });
        setMarks(updated);
    };

    // Check if there are unsaved changes
    const hasUnsavedChanges = useMemo(() => {
        const keys = Object.keys(marks);
        for (let k of keys) {
            if ((marks[k] || null) !== (originalMarks[k] || null)) return true;
        }
        return false;
    }, [marks, originalMarks]);

    // Filtered records for Daily Attendance
    const filteredRecords = useMemo(() => {
        if (!records) return [];
        return records.filter((r) => {
            if (!r || !r.staff) return false;
            const staff = r.staff;
            const id = staff._id || staff.id;
            const staffMark = marks[id];

            // Search query filter
            const q = searchQuery.toLowerCase().trim();
            if (q) {
                const matchName = staff.name?.toLowerCase().includes(q);
                const matchCode = staff.staffId?.toLowerCase().includes(q);
                const matchRole = staff.designation?.toLowerCase().includes(q);
                if (!matchName && !matchCode && !matchRole) return false;
            }

            // Status filter
            if (statusFilter === "all") return true;
            if (statusFilter === "unmarked") return !staffMark;
            return staffMark === statusFilter;
        });
    }, [records, marks, searchQuery, statusFilter]);

    // Daily KPI Metrics
    const dailyStats = useMemo(() => {
        const total = records?.length || 0;
        let present = 0;
        let halfDay = 0;
        let holiday = 0;
        let absent = 0;
        let unmarked = 0;

        (records || []).forEach((r) => {
            const id = r?.staff?._id || r?.staff?.id;
            const st = marks[id];
            if (st === "Present") present++;
            else if (st === "Half Day") halfDay++;
            else if (st === "Holiday") holiday++;
            else if (st === "Absent") absent++;
            else unmarked++;
        });

        const marked = present + halfDay + holiday + absent;
        const completionRate = total > 0 ? Math.round((marked / total) * 100) : 0;

        return { total, present, halfDay, holiday, absent, unmarked, marked, completionRate };
    }, [records, marks]);

    // Save attendance bulk
    const submitHandler = () => {
        const recordsToSave = Object.entries(marks)
            .filter(([, status]) => status)
            .map(([staffId, status]) => ({ staffId, status }));

        if (recordsToSave.length === 0) {
            toast("Please mark at least one staff member before saving", { type: "warning" });
            return;
        }

        dispatch(saveAttendanceBulk(date, recordsToSave));
    };

    // Monthly calendar navigation
    const handlePrevMonth = () => {
        if (month === 1) {
            setMonth(12);
            setYear((y) => y - 1);
        } else {
            setMonth((m) => m - 1);
        }
    };

    const handleNextMonth = () => {
        if (month === 12) {
            setMonth(1);
            setYear((y) => y + 1);
        } else {
            setMonth((m) => m + 1);
        }
    };

    // Generate monthly calendar days (DO NOT put default Sunday as holiday; only show what was marked!)
    const calendarDays = useMemo(() => {
        const totalDays = new Date(year, month, 0).getDate();
        const days = [];
        const attendanceMap = {};

        if (monthly?.attendance) {
            monthly.attendance.forEach((a) => {
                const dayNum = parseInt(a.date.split("-")[2], 10);
                attendanceMap[dayNum] = a.status;
            });
        }

        for (let d = 1; d <= totalDays; d++) {
            const dayDate = new Date(year, month - 1, d);
            const dayOfWeek = dayDate.getDay();
            const isSunday = dayOfWeek === 0;
            const isoDateString = `${year}-${String(month).padStart(2, "0")}-${String(d).padStart(2, "0")}`;
            const recordedStatus = attendanceMap[d] || null;

            days.push({
                day: d,
                weekday: WEEKDAYS[dayOfWeek],
                isSunday,
                dateStr: isoDateString,
                status: recordedStatus // Only what was marked by admin
            });
        }
        return days;
    }, [year, month, monthly]);

    // Print summary helper
    const handlePrintSummary = () => {
        window.print();
    };

    // Staff Initials Avatar helper
    const getInitials = (name) => {
        if (!name) return "ST";
        const parts = name.trim().split(" ");
        if (parts.length === 1) return parts[0].substring(0, 2).toUpperCase();
        return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
    };

    // Send Daily Attendance WhatsApp to a staff member
    const handleSendDailyWhatsApp = (staff) => {
        const id = staff._id || staff.id;
        const currentStatus = marks[id] || "Unmarked";
        const cleanPhone = sanitizeWhatsAppPhone(staff.phone);
        if (!cleanPhone) {
            toast("No phone number found for this staff member", { type: "warning" });
            return;
        }

        const msg = `🌿 *GREEN NATUROPATHY - DAILY ATTENDANCE* 🌿\n\nDear *${staff.name}* (ID: ${staff.staffId}),\n\nYour attendance for *${formatDate(date)}* is marked as: *${currentStatus}*.\n\nThank you,\n*Green Naturopathy Holistic Care*`;
        const url = `https://api.whatsapp.com/send?phone=${cleanPhone}&text=${encodeURIComponent(msg)}`;
        window.open(url, "_blank");
    };

    // Open Verification Modal & Build Message
    const handleOpenVerification = () => {
        if (!monthly || !monthly.staff) {
            toast("No staff monthly attendance data available", { type: "warning" });
            return;
        }

        const staff = monthly.staff;
        const monthName = new Date(year, month - 1, 1).toLocaleString("default", { month: "long" });
        const formattedPhone = sanitizeWhatsAppPhone(staff.phone);

        const baseSalary = staff.monthlySalary ? Number(staff.monthlySalary).toLocaleString("en-IN") : "0";
        const deduction = Number(monthly.deduction || 0).toLocaleString("en-IN");
        const payableSalary = Number(monthly.payableSalary || 0).toLocaleString("en-IN");
        const presentDays = monthly.summary?.present || 0;
        const halfDays = monthly.summary?.halfDay || 0;
        const holidays = monthly.summary?.holiday || 0;
        const leaves = monthly.summary?.absent || 0;
        const payableDays = monthly.payableDays || 0;

        const msg = `🌿 *GREEN NATUROPATHY - ATTENDANCE & PAYROLL VERIFICATION* 🌿\n\nDear *${staff.name}* (Staff ID: ${staff.staffId}),\n\nPlease review and verify your monthly attendance and salary details for *${monthName} ${year}*:\n\n📊 *Attendance Breakdown:*\n• Present Days: *${presentDays} Days*\n• Half Days: *${halfDays} Days* (0.5 day deduction)\n• Holidays / Off Days: *${holidays} Days* (Paid)\n• Leaves Taken (Absent): *${leaves} Days*\n• Total Paid Days: *${payableDays} / 30 Days*\n\n💰 *Salary Details:*\n• Monthly Base Salary: ₹${baseSalary}\n• Leave Deductions: -₹${deduction}\n• *Net Payable Salary: ₹${payableSalary}*\n\n⚠️ *Verification Request:*\nKindly review the above details. If you have any questions or attendance discrepancy, please report to Administration within 24 hours.\n\nThank you,\n*Green Naturopathy Hospital & Holistic Care*`;

        setVerifyPhone(formattedPhone);
        setVerifyMessage(msg);
        setShowVerifyModal(true);
    };

    // Send WhatsApp Verification Message
    const handleSendWhatsAppVerification = () => {
        const cleanPhone = sanitizeWhatsAppPhone(verifyPhone);
        if (!cleanPhone) {
            toast("Please enter a valid staff WhatsApp phone number", { type: "warning" });
            return;
        }
        const encoded = encodeURIComponent(verifyMessage);
        const url = `https://api.whatsapp.com/send?phone=${cleanPhone}&text=${encoded}`;
        window.open(url, "_blank");
    };

    // Copy Message to Clipboard
    const handleCopyVerificationMessage = () => {
        if (navigator?.clipboard?.writeText) {
            navigator.clipboard.writeText(verifyMessage);
            toast("Verification details copied to clipboard!", { type: "success" });
        } else {
            const el = document.createElement("textarea");
            el.value = verifyMessage;
            document.body.appendChild(el);
            el.select();
            document.execCommand("copy");
            document.body.removeChild(el);
            toast("Verification details copied to clipboard!", { type: "success" });
        }
    };

    return (
        <div className="row g-0 attendance-page-wrapper">
            <style>{`
                .attendance-page-wrapper {
                    background: #f8fafc;
                    min-height: 100vh;
                }
                .attendance-header {
                    background: #ffffff;
                    border-bottom: 1px solid #e2e8f0;
                    padding: 1.25rem 1.5rem;
                    border-radius: 12px;
                    box-shadow: 0 1px 3px rgba(0,0,0,0.04);
                }
                .attendance-pill-tabs {
                    display: inline-flex;
                    background: #f1f5f9;
                    padding: 4px;
                    border-radius: 10px;
                    border: 1px solid #e2e8f0;
                }
                .attendance-pill-btn {
                    border: none;
                    background: transparent;
                    padding: 8px 18px;
                    border-radius: 8px;
                    font-size: 0.9rem;
                    font-weight: 600;
                    color: #475569;
                    transition: all 0.2s ease;
                    cursor: pointer;
                    display: flex;
                    align-items: center;
                    gap: 8px;
                }
                .attendance-pill-btn.active {
                    background: #1b4d3e;
                    color: #ffffff;
                    box-shadow: 0 2px 4px rgba(27, 77, 62, 0.2);
                }
                .attendance-pill-btn:hover:not(.active) {
                    background: #e2e8f0;
                    color: #1e293b;
                }
                .stat-card {
                    background: #ffffff;
                    border: 1px solid #e2e8f0;
                    border-radius: 12px;
                    padding: 1rem 1.25rem;
                    display: flex;
                    align-items: center;
                    justify-content: space-between;
                    box-shadow: 0 1px 2px rgba(0,0,0,0.03);
                    transition: transform 0.15s ease, box-shadow 0.15s ease;
                }
                .stat-card:hover {
                    transform: translateY(-2px);
                    box-shadow: 0 4px 8px rgba(0,0,0,0.05);
                }
                .stat-icon {
                    width: 44px;
                    height: 44px;
                    border-radius: 10px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    font-size: 1.2rem;
                }
                .staff-avatar {
                    width: 40px;
                    height: 40px;
                    border-radius: 50%;
                    background: linear-gradient(135deg, #1b4d3e, #2d6a4f);
                    color: #ffffff;
                    font-weight: 700;
                    font-size: 0.85rem;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    flex-shrink: 0;
                }
                .status-toggle-btn {
                    padding: 6px 12px;
                    border: 1.5px solid #cbd5e1;
                    background: #ffffff;
                    color: #64748b;
                    font-weight: 600;
                    font-size: 0.8rem;
                    border-radius: 6px;
                    transition: all 0.15s ease-in-out;
                    cursor: pointer;
                    display: inline-flex;
                    align-items: center;
                    gap: 6px;
                }
                .status-toggle-btn:hover {
                    background: #f8fafc;
                    border-color: #94a3b8;
                }
                .status-toggle-btn.active-present {
                    background: #059669;
                    border-color: #059669;
                    color: #ffffff;
                    box-shadow: 0 2px 4px rgba(5, 150, 105, 0.25);
                }
                .status-toggle-btn.active-halfday {
                    background: #d97706;
                    border-color: #d97706;
                    color: #ffffff;
                    box-shadow: 0 2px 4px rgba(217, 119, 6, 0.25);
                }
                .status-toggle-btn.active-holiday {
                    background: #2563eb;
                    border-color: #2563eb;
                    color: #ffffff;
                    box-shadow: 0 2px 4px rgba(37, 99, 235, 0.25);
                }
                .status-toggle-btn.active-absent {
                    background: #dc2626;
                    border-color: #dc2626;
                    color: #ffffff;
                    box-shadow: 0 2px 4px rgba(220, 38, 38, 0.25);
                }
                .calendar-grid {
                    display: grid;
                    grid-template-columns: repeat(7, 1fr);
                    gap: 8px;
                }
                @media (max-width: 768px) {
                    .calendar-grid {
                        grid-template-columns: repeat(4, 1fr);
                    }
                }
                .calendar-day-cell {
                    border: 1px solid #e2e8f0;
                    border-radius: 8px;
                    padding: 8px 6px;
                    background: #ffffff;
                    text-align: center;
                    cursor: pointer;
                    transition: all 0.15s ease;
                }
                .calendar-day-cell:hover {
                    border-color: #1b4d3e;
                    transform: translateY(-2px);
                    box-shadow: 0 3px 6px rgba(0,0,0,0.06);
                }
                .calendar-day-cell.is-sunday {
                    background: #fdfefe;
                }
                .badge-present {
                    background: #ecfdf5;
                    color: #065f46;
                    border: 1px solid #a7f3d0;
                }
                .badge-halfday {
                    background: #fffbeb;
                    color: #92400e;
                    border: 1px solid #fde68a;
                }
                .badge-holiday {
                    background: #eff6ff;
                    color: #1d4ed8;
                    border: 1px solid #bfdbfe;
                }
                .badge-absent {
                    background: #fef2f2;
                    color: #991b1b;
                    border: 1px solid #fecaca;
                }
                .badge-unmarked {
                    background: #f1f5f9;
                    color: #64748b;
                    border: 1px solid #cbd5e1;
                }
                .sticky-bottom-bar {
                    position: sticky;
                    bottom: 0;
                    background: #ffffff;
                    border-top: 1px solid #e2e8f0;
                    padding: 12px 20px;
                    border-radius: 12px 12px 0 0;
                    box-shadow: 0 -4px 12px rgba(0,0,0,0.06);
                    z-index: 100;
                }
                .verification-modal-backdrop {
                    position: fixed;
                    top: 0;
                    left: 0;
                    width: 100vw;
                    height: 100vh;
                    background: rgba(15, 23, 42, 0.6);
                    backdrop-filter: blur(4px);
                    z-index: 1050;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    padding: 1rem;
                }
                .verification-modal-dialog {
                    background: #ffffff;
                    width: 100%;
                    max-width: 600px;
                    border-radius: 16px;
                    box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04);
                    overflow: hidden;
                    animation: modalFadeIn 0.2s ease-out;
                }
                @keyframes modalFadeIn {
                    from { opacity: 0; transform: scale(0.96); }
                    to { opacity: 1; transform: scale(1); }
                }
                @media print {
                    .no-print, .attendance-header, .sticky-bottom-bar, .attendance-pill-tabs, .sidebar-wrapper, nav, .verification-modal-backdrop {
                        display: none !important;
                    }
                    .attendance-page-wrapper {
                        background: #ffffff;
                    }
                    .main-content {
                        width: 100% !important;
                        margin: 0 !important;
                        padding: 0 !important;
                    }
                }
            `}</style>

            <div className="col-12 col-md-2 no-print">
                <Sidebar />
            </div>

            <div className="col-12 col-md-10 main-content p-3 p-md-4">
                <MetaData title={"Staff Attendance - Green Naturopathy"} />

                {/* Page Header */}
                <div className="attendance-header mb-4">
                    <div className="d-flex flex-column flex-md-row justify-content-between align-items-md-center gap-3">
                        <div>
                            <h3 className="fw-bold mb-1" style={{ color: "#1b4d3e" }}>
                                <i className="fa fa-calendar-check-o me-2"></i>Staff Attendance & Payroll
                            </h3>
                            <p className="text-muted small mb-0">
                                Record daily staff check-ins (Present, Half Day, Holiday/Off, Absent), download PDF slips, and send details to staff for verification.
                            </p>
                        </div>

                        {/* Navigation Tabs */}
                        <div className="attendance-pill-tabs no-print">
                            <button
                                type="button"
                                className={`attendance-pill-btn ${tab === "mark" ? "active" : ""}`}
                                onClick={() => setTab("mark")}
                            >
                                <i className="fa fa-check-square-o"></i>
                                <span>Daily Attendance</span>
                            </button>
                            <button
                                type="button"
                                className={`attendance-pill-btn ${tab === "history" ? "active" : ""}`}
                                onClick={() => setTab("history")}
                            >
                                <i className="fa fa-bar-chart"></i>
                                <span>Monthly History & Payroll</span>
                            </button>
                        </div>
                    </div>
                </div>

                {loading ? (
                    <div className="py-5 text-center">
                        <Loader />
                    </div>
                ) : (
                    <Fragment>
                        {/* ============================================================== */}
                        {/* TAB 1: DAILY ATTENDANCE MARKING */}
                        {/* ============================================================== */}
                        {tab === "mark" && (
                            <Fragment>
                                {/* Date Control & Quick Switcher Card */}
                                <div className="card border-0 shadow-sm rounded-3 mb-4">
                                    <div className="card-body p-3 p-md-4">
                                        <div className="row g-3 align-items-center justify-content-between">
                                            {/* Date Picker & Nav Buttons */}
                                            <div className="col-12 col-lg-5">
                                                <div className="d-flex align-items-center gap-2 flex-wrap">
                                                    <div className="btn-group shadow-sm">
                                                        <button
                                                            type="button"
                                                            className="btn btn-outline-secondary btn-sm px-2"
                                                            title="Previous Day"
                                                            onClick={() => changeDateByDays(-1)}
                                                        >
                                                            <i className="fa fa-chevron-left"></i>
                                                        </button>
                                                        <button
                                                            type="button"
                                                            className={`btn btn-sm ${isToday ? "btn-success" : "btn-outline-secondary"}`}
                                                            onClick={() => setDate(getTodayDate())}
                                                        >
                                                            Today
                                                        </button>
                                                        <button
                                                            type="button"
                                                            className="btn btn-outline-secondary btn-sm px-2"
                                                            title="Next Day"
                                                            onClick={() => changeDateByDays(1)}
                                                        >
                                                            <i className="fa fa-chevron-right"></i>
                                                        </button>
                                                    </div>

                                                    <div style={{ minWidth: 180 }}>
                                                        <AppDatePicker
                                                            label="Selected Date"
                                                            value={date}
                                                            onChange={(val) => setDate(val || getTodayDate())}
                                                        />
                                                    </div>

                                                    <div className="badge bg-light text-dark border px-2 py-1 small">
                                                        <i className="fa fa-calendar me-1 text-primary"></i>
                                                        {formatDate(date)}
                                                    </div>
                                                </div>
                                            </div>

                                            {/* Quick Bulk Action Buttons for Admin */}
                                            {isAdmin && (
                                                <div className="col-12 col-lg-7 text-lg-end">
                                                    <div className="d-flex align-items-center gap-2 justify-content-lg-end flex-wrap">
                                                        <span className="text-muted small fw-semibold me-1">1-Click Mark:</span>
                                                        <button
                                                            type="button"
                                                            className="btn btn-sm btn-outline-success rounded-pill px-3"
                                                            onClick={() => handleMarkAll("Present")}
                                                        >
                                                            <i className="fa fa-check me-1"></i>All Present
                                                        </button>
                                                        <button
                                                            type="button"
                                                            className="btn btn-sm btn-outline-primary rounded-pill px-3"
                                                            title="Mark Holiday or Sunday Off for all staff"
                                                            onClick={() => handleMarkAll("Holiday")}
                                                        >
                                                            <i className="fa fa-star me-1"></i>All Holiday / Off
                                                        </button>
                                                        <button
                                                            type="button"
                                                            className="btn btn-sm btn-outline-danger rounded-pill px-3"
                                                            onClick={() => handleMarkAll("Absent")}
                                                        >
                                                            <i className="fa fa-times me-1"></i>All Absent
                                                        </button>
                                                        <button
                                                            type="button"
                                                            className="btn btn-sm btn-outline-secondary rounded-pill px-3"
                                                            onClick={handleClearAll}
                                                        >
                                                            <i className="fa fa-undo me-1"></i>Reset
                                                        </button>
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </div>

                                {/* Daily KPI Stat Cards */}
                                <div className="row g-2 g-md-3 mb-4">
                                    <div className="col-6 col-md">
                                        <div className="stat-card">
                                            <div>
                                                <div className="text-muted small fw-medium">Total Staff</div>
                                                <div className="h4 fw-bold mb-0 text-dark">{dailyStats.total}</div>
                                            </div>
                                            <div className="stat-icon bg-light text-secondary">
                                                <i className="fa fa-users"></i>
                                            </div>
                                        </div>
                                    </div>

                                    <div className="col-6 col-md">
                                        <div className="stat-card" style={{ borderLeft: "4px solid #059669" }}>
                                            <div>
                                                <div className="text-muted small fw-medium">Present</div>
                                                <div className="h4 fw-bold mb-0 text-success">{dailyStats.present}</div>
                                            </div>
                                            <div className="stat-icon" style={{ background: "#ecfdf5", color: "#059669" }}>
                                                <i className="fa fa-check-circle"></i>
                                            </div>
                                        </div>
                                    </div>

                                    <div className="col-6 col-md">
                                        <div className="stat-card" style={{ borderLeft: "4px solid #d97706" }}>
                                            <div>
                                                <div className="text-muted small fw-medium">Half Day</div>
                                                <div className="h4 fw-bold mb-0 text-warning">{dailyStats.halfDay}</div>
                                            </div>
                                            <div className="stat-icon" style={{ background: "#fffbeb", color: "#d97706" }}>
                                                <i className="fa fa-adjust"></i>
                                            </div>
                                        </div>
                                    </div>

                                    <div className="col-6 col-md">
                                        <div className="stat-card" style={{ borderLeft: "4px solid #2563eb" }}>
                                            <div>
                                                <div className="text-muted small fw-medium">Holiday / Off</div>
                                                <div className="h4 fw-bold mb-0 text-primary">{dailyStats.holiday}</div>
                                            </div>
                                            <div className="stat-icon" style={{ background: "#eff6ff", color: "#2563eb" }}>
                                                <i className="fa fa-star"></i>
                                            </div>
                                        </div>
                                    </div>

                                    <div className="col-6 col-md">
                                        <div className="stat-card" style={{ borderLeft: "4px solid #dc2626" }}>
                                            <div>
                                                <div className="text-muted small fw-medium">Absent</div>
                                                <div className="h4 fw-bold mb-0 text-danger">{dailyStats.absent}</div>
                                            </div>
                                            <div className="stat-icon" style={{ background: "#fef2f2", color: "#dc2626" }}>
                                                <i className="fa fa-times-circle"></i>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                {/* Search & Filter Bar */}
                                <div className="card border-0 shadow-sm rounded-3 mb-3">
                                    <div className="card-body p-3">
                                        <div className="row g-2 align-items-center">
                                            <div className="col-12 col-md-5">
                                                <div className="input-group input-group-sm">
                                                    <span className="input-group-text bg-white border-end-0">
                                                        <i className="fa fa-search text-muted"></i>
                                                    </span>
                                                    <input
                                                        type="text"
                                                        className="form-control border-start-0 ps-0"
                                                        placeholder="Search staff by name, ID or role..."
                                                        value={searchQuery}
                                                        onChange={(e) => setSearchQuery(e.target.value)}
                                                    />
                                                    {searchQuery && (
                                                        <button
                                                            className="btn btn-outline-secondary"
                                                            type="button"
                                                            onClick={() => setSearchQuery("")}
                                                        >
                                                            <i className="fa fa-times"></i>
                                                        </button>
                                                    )}
                                                </div>
                                            </div>

                                            <div className="col-12 col-md-7 text-md-end">
                                                <div className="d-flex align-items-center gap-1 justify-content-md-end flex-wrap">
                                                    <span className="text-muted small me-1">Filter:</span>
                                                    {[
                                                        { key: "all", label: "All" },
                                                        { key: "Present", label: "Present" },
                                                        { key: "Half Day", label: "Half Day" },
                                                        { key: "Holiday", label: "Holiday/Off" },
                                                        { key: "Absent", label: "Absent" },
                                                        { key: "unmarked", label: `Unmarked (${dailyStats.unmarked})` }
                                                    ].map((f) => (
                                                        <button
                                                            key={f.key}
                                                            type="button"
                                                            className={`btn btn-sm rounded-pill px-2 py-0 ${statusFilter === f.key ? "btn-dark" : "btn-light text-secondary border"}`}
                                                            style={{ fontSize: "0.78rem" }}
                                                            onClick={() => setStatusFilter(f.key)}
                                                        >
                                                            {f.label}
                                                        </button>
                                                    ))}
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                {/* Staff Attendance Table / List */}
                                <div className="card border-0 shadow-sm rounded-3 mb-4 overflow-hidden">
                                    <div className="table-responsive">
                                        <table className="table table-hover align-middle mb-0">
                                            <thead style={{ background: "#f8fafc", borderBottom: "2px solid #e2e8f0" }}>
                                                <tr>
                                                    <th className="py-3 px-3 text-secondary small text-uppercase" style={{ width: 80 }}>Staff</th>
                                                    <th className="py-3 px-3 text-secondary small text-uppercase">Employee Info</th>
                                                    <th className="py-3 px-3 text-secondary small text-uppercase">Designation</th>
                                                    <th className="py-3 px-3 text-secondary small text-uppercase text-center" style={{ minWidth: 320 }}>
                                                        Attendance (4 Options)
                                                    </th>
                                                    <th className="py-3 px-3 text-secondary small text-uppercase text-end" style={{ width: 140 }}>Quick View</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {filteredRecords.length > 0 ? (
                                                    filteredRecords.map(({ staff }) => {
                                                        const id = staff._id || staff.id;
                                                        const currentStatus = marks[id];
                                                        const initials = getInitials(staff.name);

                                                        return (
                                                            <tr key={id}>
                                                                <td className="px-3">
                                                                    <div className="staff-avatar">{initials}</div>
                                                                </td>
                                                                <td className="px-3">
                                                                    <div className="fw-bold text-dark">{staff.name}</div>
                                                                    <div className="text-muted small d-flex align-items-center gap-1 flex-wrap">
                                                                        <span className="badge bg-light text-secondary border">ID: {staff.staffId}</span>
                                                                        {staff.phone && (
                                                                            <span>
                                                                                <i className="fa fa-phone me-1 text-muted"></i>{staff.phone}
                                                                                <button
                                                                                    type="button"
                                                                                    className="btn btn-xs btn-outline-success py-0 px-1 ms-1 rounded-pill"
                                                                                    title="Send Daily Attendance update via WhatsApp"
                                                                                    style={{ fontSize: "0.7rem", lineHeight: "1.2" }}
                                                                                    onClick={() => handleSendDailyWhatsApp(staff)}
                                                                                >
                                                                                    <i className="fab fa-whatsapp me-1"></i>Share
                                                                                </button>
                                                                            </span>
                                                                        )}
                                                                    </div>
                                                                </td>
                                                                <td className="px-3">
                                                                    <span className="badge bg-light text-dark border px-2 py-1">
                                                                        {staff.designation || "Staff Member"}
                                                                    </span>
                                                                </td>
                                                                <td className="px-3 text-center">
                                                                    {isAdmin ? (
                                                                        <div className="d-inline-flex gap-1 p-1 bg-light rounded-3 border flex-wrap justify-content-center">
                                                                            {STATUSES.map((st) => {
                                                                                const cfg = STATUS_CONFIG[st];
                                                                                const isActive = currentStatus === st;
                                                                                let activeClass = "";
                                                                                if (isActive) {
                                                                                    if (st === "Present") activeClass = "active-present";
                                                                                    else if (st === "Half Day") activeClass = "active-halfday";
                                                                                    else if (st === "Holiday") activeClass = "active-holiday";
                                                                                    else if (st === "Absent") activeClass = "active-absent";
                                                                                }
                                                                                return (
                                                                                    <button
                                                                                        key={st}
                                                                                        type="button"
                                                                                        className={`status-toggle-btn ${activeClass}`}
                                                                                        title={cfg.desc}
                                                                                        onClick={() => handleSetMark(id, st)}
                                                                                    >
                                                                                        <i className={`fa ${cfg.icon}`}></i>
                                                                                        {st}
                                                                                    </button>
                                                                                );
                                                                            })}
                                                                        </div>
                                                                    ) : (
                                                                        <div>
                                                                            {currentStatus ? (
                                                                                <span className={`badge ${STATUS_CONFIG[currentStatus]?.badgeClass || "bg-secondary"} px-3 py-2 fs-6`}>
                                                                                    <i className={`fa ${STATUS_CONFIG[currentStatus]?.icon} me-1`}></i>
                                                                                    {currentStatus}
                                                                                </span>
                                                                            ) : (
                                                                                <span className="badge bg-light text-muted border px-2 py-1">Unmarked</span>
                                                                            )}
                                                                        </div>
                                                                    )}
                                                                </td>
                                                                <td className="px-3 text-end">
                                                                    <button
                                                                        type="button"
                                                                        className="btn btn-sm btn-outline-primary"
                                                                        title="View Monthly Record & Payroll"
                                                                        onClick={() => {
                                                                            setHistoryStaffId(id);
                                                                            setTab("history");
                                                                        }}
                                                                    >
                                                                        <i className="fa fa-history me-1"></i>History
                                                                    </button>
                                                                </td>
                                                            </tr>
                                                        );
                                                    })
                                                ) : (
                                                    <tr>
                                                        <td colSpan="5" className="text-center py-5 text-muted">
                                                            <i className="fa fa-folder-open-o fa-3x mb-2 text-secondary d-block"></i>
                                                            No staff members match the selected filter.
                                                        </td>
                                                    </tr>
                                                )}
                                            </tbody>
                                        </table>
                                    </div>
                                </div>

                                {/* Floating/Sticky Action Bar for Saving Attendance */}
                                {isAdmin && (
                                    <div className="sticky-bottom-bar d-flex align-items-center justify-content-between flex-wrap gap-2">
                                        <div className="d-flex align-items-center gap-3">
                                            <span className="badge bg-success-subtle text-success border px-2 py-1">
                                                <i className="fa fa-check-circle me-1"></i>{dailyStats.marked} / {dailyStats.total} Marked
                                            </span>
                                            {dailyStats.unmarked > 0 && (
                                                <span className="badge bg-warning-subtle text-warning-emphasis border px-2 py-1">
                                                    <i className="fa fa-exclamation-circle me-1"></i>{dailyStats.unmarked} Unmarked
                                                </span>
                                            )}
                                            {hasUnsavedChanges && (
                                                <span className="badge bg-info-subtle text-info-emphasis border px-2 py-1">
                                                    <i className="fa fa-pencil me-1"></i>Unsaved Changes
                                                </span>
                                            )}
                                        </div>

                                        <button
                                            type="button"
                                            className="btn btn-success px-4 py-2 fw-semibold shadow-sm"
                                            style={{ background: "#1b4d3e", borderColor: "#1b4d3e" }}
                                            onClick={submitHandler}
                                            disabled={loading}
                                        >
                                            <i className="fa fa-save me-2"></i>Save Attendance
                                        </button>
                                    </div>
                                )}
                            </Fragment>
                        )}

                        {/* ============================================================== */}
                        {/* TAB 2: MONTHLY HISTORY & PAYROLL BREAKDOWN */}
                        {/* ============================================================== */}
                        {tab === "history" && (
                            <Fragment>
                                {/* Filter Card: Staff Selection, Month & Year Controls */}
                                <div className="card border-0 shadow-sm rounded-3 mb-4">
                                    <div className="card-body p-3 p-md-4">
                                        <div className="row g-3 align-items-center">
                                            <div className="col-12 col-md-5">
                                                <label className="form-label small fw-semibold text-muted mb-1">
                                                    Select Staff Member
                                                </label>
                                                <div className="input-group">
                                                    <span className="input-group-text bg-white">
                                                        <i className="fa fa-user text-muted"></i>
                                                    </span>
                                                    <select
                                                        className="form-select"
                                                        value={historyStaffId}
                                                        onChange={(e) => setHistoryStaffId(e.target.value)}
                                                    >
                                                        {(staffList || []).map((s) => {
                                                            const sId = s._id || s.id;
                                                            return (
                                                                <option key={sId} value={sId}>
                                                                    {s.staffId} - {s.name} ({s.designation || "Staff"})
                                                                </option>
                                                            );
                                                        })}
                                                    </select>
                                                </div>
                                            </div>

                                            <div className="col-7 col-md-3">
                                                <label className="form-label small fw-semibold text-muted mb-1">
                                                    Select Month & Year
                                                </label>
                                                <div className="d-flex align-items-center gap-1">
                                                    <button
                                                        type="button"
                                                        className="btn btn-outline-secondary btn-sm"
                                                        title="Previous Month"
                                                        onClick={handlePrevMonth}
                                                    >
                                                        <i className="fa fa-chevron-left"></i>
                                                    </button>
                                                    <select
                                                        className="form-select form-select-sm"
                                                        value={month}
                                                        onChange={(e) => setMonth(Number(e.target.value))}
                                                    >
                                                        {Array.from({ length: 12 }, (_, i) => i + 1).map((m) => (
                                                            <option key={m} value={m}>
                                                                {new Date(2000, m - 1, 1).toLocaleString("default", { month: "long" })}
                                                            </option>
                                                        ))}
                                                    </select>
                                                    <input
                                                        type="number"
                                                        className="form-control form-control-sm"
                                                        style={{ width: 85 }}
                                                        value={year}
                                                        onChange={(e) => setYear(Number(e.target.value))}
                                                    />
                                                    <button
                                                        type="button"
                                                        className="btn btn-outline-secondary btn-sm"
                                                        title="Next Month"
                                                        onClick={handleNextMonth}
                                                    >
                                                        <i className="fa fa-chevron-right"></i>
                                                    </button>
                                                </div>
                                            </div>

                                            <div className="col-12 col-md-4 text-md-end pt-md-4">
                                                <div className="d-flex align-items-center gap-2 justify-content-md-end flex-wrap">
                                                    <button
                                                        type="button"
                                                        className="btn btn-outline-success btn-sm rounded-pill px-3 shadow-sm"
                                                        onClick={() => downloadPaySlipPDF(monthly.staff, monthly, year, month)}
                                                        title="Download Official Salary Payslip (PDF)"
                                                    >
                                                        <i className="fa fa-file-text-o me-1"></i>Pay Slip
                                                    </button>
                                                    <button
                                                        type="button"
                                                        className="btn btn-outline-primary btn-sm rounded-pill px-3 shadow-sm"
                                                        onClick={() => downloadAttendancePDF(monthly, year, month)}
                                                        title="Download Detailed Attendance Statement (PDF)"
                                                    >
                                                        <i className="fa fa-file-pdf-o me-1"></i>Attendance PDF
                                                    </button>
                                                    <button
                                                        type="button"
                                                        className="btn btn-outline-dark btn-sm rounded-pill px-3 shadow-sm"
                                                        onClick={handlePrintSummary}
                                                        title="Print Monthly Attendance Slip"
                                                    >
                                                        <i className="fa fa-print me-1"></i>Print
                                                    </button>
                                                    <button
                                                        type="button"
                                                        className="btn btn-success btn-sm rounded-pill px-3 shadow-sm text-white"
                                                        style={{ background: "#25D366", borderColor: "#25D366" }}
                                                        onClick={handleOpenVerification}
                                                        title="Send details to staff for verification via WhatsApp"
                                                    >
                                                        <i className="fab fa-whatsapp me-1"></i>Send to Staff
                                                    </button>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                {monthly && monthly.staff ? (
                                    <Fragment>
                                        {/* Staff Information & Quick Summary Banner */}
                                        <div className="card border-0 shadow-sm rounded-3 mb-4" style={{ background: "linear-gradient(135deg, #1b4d3e, #2d6a4f)", color: "#ffffff" }}>
                                            <div className="card-body p-3 p-md-4">
                                                <div className="d-flex flex-column flex-md-row justify-content-between align-items-md-center gap-3">
                                                    <div className="d-flex align-items-center gap-3">
                                                        <div
                                                            className="rounded-circle d-flex align-items-center justify-content-center text-dark fw-bold"
                                                            style={{ width: 54, height: 54, background: "#ffffff", fontSize: "1.2rem" }}
                                                        >
                                                            {getInitials(monthly.staff.name)}
                                                        </div>
                                                        <div>
                                                            <h4 className="fw-bold mb-1 text-white">{monthly.staff.name}</h4>
                                                            <div className="text-light small opacity-75">
                                                                <span className="badge bg-light text-dark me-2">ID: {monthly.staff.staffId}</span>
                                                                <span>{monthly.staff.designation || "Staff"}</span>
                                                                {monthly.staff.phone && <span className="ms-2"><i className="fa fa-phone me-1"></i>{monthly.staff.phone}</span>}
                                                            </div>
                                                        </div>
                                                    </div>

                                                    <div className="d-flex flex-column flex-md-row align-items-md-center gap-2 text-md-end">
                                                        <div className="me-md-2">
                                                            <div className="text-light small opacity-75">Payroll Statement for</div>
                                                            <div className="h5 fw-bold mb-0 text-white">
                                                                {new Date(year, month - 1, 1).toLocaleString("default", { month: "long" })} {year}
                                                            </div>
                                                        </div>
                                                        <button
                                                            type="button"
                                                            className="btn btn-sm btn-light fw-semibold shadow-sm d-inline-flex align-items-center gap-1"
                                                            style={{ color: "#1b4d3e" }}
                                                            onClick={() => downloadAttendancePDF(monthly, year, month)}
                                                            title="Download PDF statement"
                                                        >
                                                            <i className="fa fa-file-pdf-o text-danger"></i>
                                                            <span>PDF</span>
                                                        </button>
                                                        <button
                                                            type="button"
                                                            className="btn btn-sm btn-light fw-semibold shadow-sm d-inline-flex align-items-center gap-1"
                                                            style={{ color: "#1b4d3e" }}
                                                            onClick={handleOpenVerification}
                                                            title="Send details to staff via WhatsApp"
                                                        >
                                                            <i className="fab fa-whatsapp text-success fs-6"></i>
                                                            <span>Send for Verification</span>
                                                        </button>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>

                                        {/* Monthly KPI Metrics & Salary Breakdown Cards */}
                                        <div className="row g-2 g-md-3 mb-4">
                                            <div className="col-6 col-md-3 col-xl-2">
                                                <div className="stat-card">
                                                    <div>
                                                        <div className="text-muted small fw-medium">Base Salary</div>
                                                        <div className="fw-bold fs-6 text-dark">
                                                            ₹{monthly.staff.monthlySalary ? Number(monthly.staff.monthlySalary).toLocaleString("en-IN") : 0}
                                                        </div>
                                                    </div>
                                                    <div className="stat-icon bg-light text-secondary">
                                                        <i className="fa fa-money"></i>
                                                    </div>
                                                </div>
                                            </div>

                                            <div className="col-6 col-md-3 col-xl-2">
                                                <div className="stat-card" style={{ borderLeft: "4px solid #059669" }}>
                                                    <div>
                                                        <div className="text-muted small fw-medium">Present</div>
                                                        <div className="h5 fw-bold mb-0 text-success">{monthly.summary?.present || 0} Days</div>
                                                    </div>
                                                    <div className="stat-icon" style={{ background: "#ecfdf5", color: "#059669" }}>
                                                        <i className="fa fa-check"></i>
                                                    </div>
                                                </div>
                                            </div>

                                            <div className="col-6 col-md-3 col-xl-2">
                                                <div className="stat-card" style={{ borderLeft: "4px solid #d97706" }}>
                                                    <div>
                                                        <div className="text-muted small fw-medium">Half Day (0.5)</div>
                                                        <div className="h5 fw-bold mb-0 text-warning">{monthly.summary?.halfDay || 0} Days</div>
                                                    </div>
                                                    <div className="stat-icon" style={{ background: "#fffbeb", color: "#d97706" }}>
                                                        <i className="fa fa-adjust"></i>
                                                    </div>
                                                </div>
                                            </div>

                                            <div className="col-6 col-md-3 col-xl-2">
                                                <div className="stat-card" style={{ borderLeft: "4px solid #2563eb" }}>
                                                    <div>
                                                        <div className="text-muted small fw-medium">Holidays / Off</div>
                                                        <div className="h5 fw-bold mb-0 text-primary">{monthly.summary?.holiday || 0} Days</div>
                                                    </div>
                                                    <div className="stat-icon" style={{ background: "#eff6ff", color: "#2563eb" }}>
                                                        <i className="fa fa-star"></i>
                                                    </div>
                                                </div>
                                            </div>

                                            <div className="col-6 col-md-3 col-xl-2">
                                                <div className="stat-card" style={{ borderLeft: "4px solid #dc2626" }}>
                                                    <div>
                                                        <div className="text-muted small fw-medium">Leaves (Absent)</div>
                                                        <div className="h5 fw-bold mb-0 text-danger">{monthly.summary?.absent || 0} Days</div>
                                                    </div>
                                                    <div className="stat-icon" style={{ background: "#fef2f2", color: "#dc2626" }}>
                                                        <i className="fa fa-times"></i>
                                                    </div>
                                                </div>
                                            </div>

                                            <div className="col-6 col-md-3 col-xl-2">
                                                <div className="stat-card" style={{ borderLeft: "4px solid #0284c7" }}>
                                                    <div>
                                                        <div className="text-muted small fw-medium">Paid Days</div>
                                                        <div className="h5 fw-bold mb-0 text-primary">{monthly.payableDays || 0} / 30</div>
                                                    </div>
                                                    <div className="stat-icon" style={{ background: "#e0f2fe", color: "#0284c7" }}>
                                                        <i className="fa fa-calendar-check-o"></i>
                                                    </div>
                                                </div>
                                            </div>

                                            <div className="col-6 col-md-3 col-xl-2">
                                                <div className="stat-card" style={{ borderLeft: "4px solid #e11d48", background: "#fff1f2" }}>
                                                    <div>
                                                        <div className="text-muted small fw-medium">Leave Deduction</div>
                                                        <div className="h5 fw-bold mb-0 text-danger">
                                                            -₹{Number(monthly.deduction || 0).toLocaleString("en-IN")}
                                                        </div>
                                                    </div>
                                                    <div className="stat-icon" style={{ background: "#ffe4e6", color: "#e11d48" }}>
                                                        <i className="fa fa-minus-circle"></i>
                                                    </div>
                                                </div>
                                            </div>

                                            <div className="col-12 col-md-6 col-xl-4">
                                                <div className="stat-card" style={{ borderLeft: "4px solid #16a34a", background: "#f0fdf4" }}>
                                                    <div>
                                                        <div className="text-muted small fw-medium">Net Payable Salary</div>
                                                        <div className="h4 fw-bold mb-0 text-success">
                                                            ₹{Number(monthly.payableSalary || 0).toLocaleString("en-IN")}
                                                        </div>
                                                    </div>
                                                    <div className="stat-icon" style={{ background: "#dcfce7", color: "#16a34a" }}>
                                                        <i className="fa fa-check-square"></i>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>

                                        {/* Policy Banner */}
                                        <div className="alert alert-light border shadow-sm rounded-3 py-2 px-3 mb-4 d-flex align-items-center gap-2">
                                            <i className="fa fa-info-circle text-primary fs-5"></i>
                                            <span className="small text-muted">
                                                <strong>Attendance & Payroll Rules:</strong> Present = Full Day Paid, Half Day = 0.5 Day Deducted, Holiday = Paid (Festivals / Sunday Offs marked as Holiday), Absent = 1.0 Day Deducted. Based on standard 30-day month.
                                            </span>
                                        </div>

                                        {/* Visual Calendar View */}
                                        <div className="card border-0 shadow-sm rounded-3 mb-4">
                                            <div className="card-header bg-white border-bottom py-3 d-flex flex-column flex-md-row justify-content-between align-items-md-center gap-2">
                                                <h6 className="fw-bold mb-0 text-dark">
                                                    <i className="fa fa-calendar me-2 text-primary"></i>
                                                    Monthly Attendance Calendar Grid
                                                </h6>
                                                <div className="d-flex align-items-center gap-2 flex-wrap small text-muted">
                                                    <span className="badge badge-present px-2 py-1">P = Present</span>
                                                    <span className="badge badge-halfday px-2 py-1">HD = Half Day</span>
                                                    <span className="badge badge-holiday px-2 py-1">H = Holiday / Off</span>
                                                    <span className="badge badge-absent px-2 py-1">A = Absent / Leave</span>
                                                    <span className="badge badge-unmarked px-2 py-1">- = Unmarked</span>
                                                </div>
                                            </div>
                                            <div className="card-body p-3 p-md-4">
                                                <div className="calendar-grid">
                                                    {calendarDays.map((item) => {
                                                        let badgeClass = "badge-unmarked";
                                                        let label = "-";
                                                        if (item.status === "Present") {
                                                            badgeClass = "badge-present";
                                                            label = "P";
                                                        } else if (item.status === "Half Day") {
                                                            badgeClass = "badge-halfday";
                                                            label = "HD";
                                                        } else if (item.status === "Holiday") {
                                                            badgeClass = "badge-holiday";
                                                            label = "H";
                                                        } else if (item.status === "Absent") {
                                                            badgeClass = "badge-absent";
                                                            label = "A";
                                                        }

                                                        return (
                                                            <div
                                                                key={item.day}
                                                                className={`calendar-day-cell ${item.isSunday ? "is-sunday" : ""}`}
                                                                title={`Click to view or edit attendance for ${item.dateStr} (${item.status || "Unmarked"})`}
                                                                onClick={() => {
                                                                    setDate(item.dateStr);
                                                                    setTab("mark");
                                                                }}
                                                            >
                                                                <div className="d-flex justify-content-between align-items-center mb-1">
                                                                    <span className="fw-bold text-dark small">{item.day}</span>
                                                                    <span className={item.isSunday ? "text-danger fw-semibold" : "text-muted"} style={{ fontSize: "0.7rem" }}>
                                                                        {item.weekday}
                                                                    </span>
                                                                </div>
                                                                <span className={`badge ${badgeClass} w-100 py-1`} style={{ fontSize: "0.75rem" }}>
                                                                    {label}
                                                                </span>
                                                            </div>
                                                        );
                                                    })}
                                                </div>
                                                <div className="text-muted small mt-2">
                                                    <i className="fa fa-info-circle me-1"></i>
                                                    Tip: Click on any day cell to jump directly to that date in the Daily Attendance tab.
                                                </div>
                                            </div>
                                        </div>

                                        {/* Detailed Attendance Log Table */}
                                        <div className="card border-0 shadow-sm rounded-3 mb-4 overflow-hidden">
                                            <div className="card-header bg-white border-bottom py-3">
                                                <h6 className="fw-bold mb-0 text-dark">
                                                    <i className="fa fa-list me-2 text-secondary"></i>
                                                    Detailed Attendance History Log
                                                </h6>
                                            </div>
                                            <div className="table-responsive">
                                                <table className="table table-hover align-middle mb-0">
                                                    <thead style={{ background: "#f8fafc" }}>
                                                        <tr>
                                                            <th className="py-2 px-3 small text-secondary">Date</th>
                                                            <th className="py-2 px-3 small text-secondary">Day of Week</th>
                                                            <th className="py-2 px-3 small text-secondary">Status</th>
                                                            {isAdmin && <th className="py-2 px-3 small text-secondary text-end">Action</th>}
                                                        </tr>
                                                    </thead>
                                                    <tbody>
                                                        {monthly.attendance?.length > 0 ? (
                                                            monthly.attendance.map((a) => {
                                                                const dayOfWeek = new Date(a.date).toLocaleDateString("en-US", { weekday: "long" });
                                                                const cfg = STATUS_CONFIG[a.status] || { badgeClass: "bg-secondary", icon: "fa-circle" };

                                                                return (
                                                                    <tr key={a._id || a.id || a.date}>
                                                                        <td className="px-3 fw-semibold">{formatDate(a.date)}</td>
                                                                        <td className="px-3 text-muted">{dayOfWeek}</td>
                                                                        <td className="px-3">
                                                                            <span className={`badge ${cfg.badgeClass} px-3 py-2`}>
                                                                                <i className={`fa ${cfg.icon} me-1`}></i>
                                                                                {a.status}
                                                                            </span>
                                                                        </td>
                                                                        {isAdmin && (
                                                                            <td className="px-3 text-end">
                                                                                <button
                                                                                    type="button"
                                                                                    className="btn btn-sm btn-outline-primary"
                                                                                    onClick={() => {
                                                                                        setDate(toInputDate(a.date));
                                                                                        setTab("mark");
                                                                                    }}
                                                                                >
                                                                                    <i className="fa fa-pencil me-1"></i>Edit
                                                                                </button>
                                                                            </td>
                                                                        )}
                                                                    </tr>
                                                                );
                                                            })
                                                        ) : (
                                                            <tr>
                                                                <td colSpan={isAdmin ? 4 : 3} className="text-center py-4 text-muted">
                                                                    No attendance records found for this staff member in this month.
                                                                </td>
                                                            </tr>
                                                        )}
                                                    </tbody>
                                                </table>
                                            </div>
                                        </div>
                                    </Fragment>
                                ) : (
                                    <div className="card border-0 shadow-sm rounded-3 p-5 text-center text-muted">
                                        <i className="fa fa-user-circle-o fa-3x mb-3 text-secondary"></i>
                                        <h5>No Staff Selected</h5>
                                        <p className="mb-0">Please select an active staff member to view their attendance history and payroll breakdown.</p>
                                    </div>
                                )}
                            </Fragment>
                        )}
                    </Fragment>
                )}

                {/* ============================================================== */}
                {/* VERIFICATION MODAL (SEND VIA WHATSAPP / COPY DETAILS / PDF) */}
                {/* ============================================================== */}
                {showVerifyModal && (
                    <div className="verification-modal-backdrop" onClick={() => setShowVerifyModal(false)}>
                        <div className="verification-modal-dialog" onClick={(e) => e.stopPropagation()}>
                            <div className="p-3 px-4 border-bottom d-flex align-items-center justify-content-between" style={{ background: "#f8fafc" }}>
                                <div className="d-flex align-items-center gap-2">
                                    <div className="rounded-circle d-flex align-items-center justify-content-center text-white" style={{ width: 38, height: 38, background: "#25D366" }}>
                                        <i className="fab fa-whatsapp fs-5"></i>
                                    </div>
                                    <div>
                                        <h6 className="fw-bold mb-0 text-dark">Send Details for Staff Verification</h6>
                                        <small className="text-muted">Deliver monthly attendance & payroll breakdown directly to staff</small>
                                    </div>
                                </div>
                                <button
                                    type="button"
                                    className="btn-close"
                                    aria-label="Close"
                                    onClick={() => setShowVerifyModal(false)}
                                ></button>
                            </div>

                            <div className="p-4">
                                <div className="mb-3">
                                    <label className="form-label small fw-semibold text-muted mb-1">
                                        Staff WhatsApp Number
                                    </label>
                                    <div className="input-group">
                                        <span className="input-group-text bg-white">
                                            <i className="fab fa-whatsapp text-success me-1"></i>
                                            <span className="small text-muted">+</span>
                                        </span>
                                        <input
                                            type="text"
                                            className="form-control"
                                            placeholder="e.g. 919876543210"
                                            value={verifyPhone}
                                            onChange={(e) => setVerifyPhone(e.target.value)}
                                        />
                                    </div>
                                    <small className="text-muted" style={{ fontSize: "0.75rem" }}>
                                        Country code included (e.g. 91 for India). You can adjust the number if needed.
                                    </small>
                                </div>

                                <div className="mb-3">
                                    <label className="form-label small fw-semibold text-muted mb-1">
                                        Verification Message Preview
                                    </label>
                                    <textarea
                                        className="form-control font-monospace"
                                        rows={9}
                                        style={{ fontSize: "0.82rem", background: "#f8fafc", resize: "vertical" }}
                                        value={verifyMessage}
                                        onChange={(e) => setVerifyMessage(e.target.value)}
                                    ></textarea>
                                </div>
                            </div>

                            <div className="p-3 px-4 bg-light border-top d-flex align-items-center justify-content-between flex-wrap gap-2">
                                <div className="d-flex align-items-center gap-2">
                                    <button
                                        type="button"
                                        className="btn btn-outline-secondary btn-sm rounded-pill px-3"
                                        onClick={handleCopyVerificationMessage}
                                        title="Copy text to clipboard"
                                    >
                                        <i className="fa fa-copy me-1"></i>Copy Text
                                    </button>
                                    <button
                                        type="button"
                                        className="btn btn-outline-success btn-sm rounded-pill px-3"
                                        onClick={() => downloadPaySlipPDF(monthly.staff, monthly, year, month)}
                                        title="Download Salary Pay Slip PDF"
                                    >
                                        <i className="fa fa-file-text-o me-1"></i>Pay Slip
                                    </button>
                                    <button
                                        type="button"
                                        className="btn btn-outline-primary btn-sm rounded-pill px-3"
                                        onClick={() => downloadAttendancePDF(monthly, year, month)}
                                        title="Download Detailed Attendance Statement (PDF)"
                                    >
                                        <i className="fa fa-file-pdf-o me-1"></i>Attendance PDF
                                    </button>
                                </div>

                                <div className="d-flex align-items-center gap-2">
                                    <button
                                        type="button"
                                        className="btn btn-light btn-sm rounded-pill px-3 border"
                                        onClick={() => setShowVerifyModal(false)}
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        type="button"
                                        className="btn btn-success btn-sm rounded-pill px-4 text-white fw-semibold shadow-sm"
                                        style={{ background: "#25D366", borderColor: "#25D366" }}
                                        onClick={handleSendWhatsAppVerification}
                                    >
                                        <i className="fab fa-whatsapp me-1"></i>Send via WhatsApp
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}
